import { useState, useEffect } from 'react';
import { Download, Eye, FileArchive, Calendar, Mail, Phone, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Submission {
  id: string;
  name: string;
  email: string;
  phone: string;
  app_type: string;
  description: string;
  file_path: string;
  file_name: string;
  file_size: number;
  status: string;
  created_at: string;
}

export function AdminPage() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('source-code')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Không thể tải file. Vui lòng thử lại.');
    }
  };

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewing': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ xử lý';
      case 'reviewing': return 'Đang xem xét';
      case 'approved': return 'Đã duyệt';
      case 'rejected': return 'Từ chối';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Quản lý Submissions</h1>
          <p className="text-slate-600">Tổng số: {submissions.length} yêu cầu</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Khách hàng</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Loại app</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">File</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Trạng thái</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Ngày gửi</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Hành động</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {submissions.map((submission) => (
                  <tr key={submission.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-slate-900">{submission.name}</div>
                        <div className="text-sm text-slate-600 flex items-center mt-1">
                          <Mail className="w-3 h-3 mr-1" />
                          {submission.email}
                        </div>
                        <div className="text-sm text-slate-600 flex items-center mt-0.5">
                          <Phone className="w-3 h-3 mr-1" />
                          {submission.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 text-sm font-medium">
                        {submission.app_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <FileArchive className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm font-medium text-slate-900 truncate max-w-[150px]">
                            {submission.file_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {formatFileSize(submission.file_size)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={submission.status}
                        onChange={(e) => updateStatus(submission.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(submission.status)} border-0 cursor-pointer`}
                      >
                        <option value="pending">Chờ xử lý</option>
                        <option value="reviewing">Đang xem xét</option>
                        <option value="approved">Đã duyệt</option>
                        <option value="rejected">Từ chối</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-slate-600">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(submission.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedSubmission(submission)}
                          className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                          title="Xem chi tiết"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => downloadFile(submission.file_path, submission.file_name)}
                          className="p-2 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                          title="Tải xuống"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {submissions.length === 0 && (
            <div className="text-center py-12">
              <FileArchive className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Chưa có submission nào</p>
            </div>
          )}
        </div>
      </div>

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Chi tiết Submission</h3>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-500">Khách hàng</label>
                <p className="text-lg text-slate-900 font-medium">{selectedSubmission.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Email</label>
                  <p className="text-slate-900">{selectedSubmission.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Số điện thoại</label>
                  <p className="text-slate-900">{selectedSubmission.phone}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Loại ứng dụng</label>
                <p className="text-slate-900 capitalize">{selectedSubmission.app_type}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">Mô tả</label>
                <p className="text-slate-900 whitespace-pre-wrap">
                  {selectedSubmission.description || 'Không có mô tả'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-500">File đã upload</label>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mt-2">
                  <div className="flex items-center space-x-3">
                    <FileArchive className="w-8 h-8 text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-900">{selectedSubmission.file_name}</p>
                      <p className="text-sm text-slate-500">{formatFileSize(selectedSubmission.file_size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => downloadFile(selectedSubmission.file_path, selectedSubmission.file_name)}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <Download className="w-4 h-4" />
                    <span>Tải xuống</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-500">Trạng thái</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-1 ${getStatusColor(selectedSubmission.status)}`}>
                    {getStatusText(selectedSubmission.status)}
                  </span>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-500">Ngày gửi</label>
                  <p className="text-slate-900 mt-1">{formatDate(selectedSubmission.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
